// Safe area, классы ширины и высоты, ориентация, полноэкранный режим (ux/12 §1–§3, ВР-76).
// Итоговые отступы: верх = safeArea.top + contentSafeArea.top; низ = safeArea.bottom; бока = max(16, safeArea).
// Без API (клиенты ниже 8.0) — CSS env(safe-area-inset-*), это задано в стилях как запасное значение.
import { signal } from '@preact/signals';
import { track } from '../analytics/analytics.js';
import { storage } from '../state/storage.js';
import { isPhone, versionAtLeast, type TgWebApp } from './webapp.js';

export type WidthClass = 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL';
export type HeightClass = 'normal' | 'compact' | 'tiny';

export function widthClass(w: number): WidthClass {
  if (w < 390) return 'XS';
  if (w < 430) return 'S';
  if (w < 768) return 'M';
  if (w < 1024) return 'L';
  if (w < 1440) return 'XL';
  return 'XXL';
}

/** ux/12 §1.1: ≥ 600 — обычная, 450–599 — компактная, ниже — ещё компактнее; блокировки нет никогда. */
export function heightClass(h: number): HeightClass {
  if (h >= 600) return 'normal';
  if (h >= 450) return 'compact';
  return 'tiny';
}

/** OVL-ROTATE — только альбом на телефоне (короткая сторона < 600), никогда по высоте (ux/12 §2, X-27). */
export function needsRotate(p: { w: number; h: number; phone: boolean }): boolean {
  return p.phone && p.w > p.h && Math.min(p.w, p.h) < 600;
}

export const FULLSCREEN_KEY = 'fullscreen';

/**
 * ВР-76, расширен ВР-84: на любой платформе с Bot API 8.0+ (телефон, Telegram Desktop, Telegram Web) полноэкранный
 * режим включается при старте, если игрок не выключил его в настройках (localStorage) и конфиг
 * `client.fullscreen_default` не равен false.
 */
export function shouldRequestFullscreen(p: { api8: boolean; stored: string | null; configDefault: boolean }): boolean {
  if (!p.api8) return false;
  if (p.stored === 'off') return false;
  if (p.stored === 'on') return true;
  return p.configDefault;
}

export const viewport = signal({ w: 390, h: 844, width: 'S' as WidthClass, height: 'normal' as HeightClass, rotate: false, fullscreen: false });

export function initViewport(wa: TgWebApp | null, root: HTMLElement = document.documentElement): () => void {
  const phone = isPhone(wa) || (!wa && matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 600);
  let raf = 0;
  const apply = () => {
    raf = 0;
    const api8 = wa ? versionAtLeast(wa, '8.0') : false;
    const sa = api8 ? wa?.safeAreaInset : undefined;
    const csa = api8 ? wa?.contentSafeAreaInset : undefined;
    const set = (k: string, v: number | undefined, fallback: string) =>
      root.style.setProperty(k, v === undefined ? fallback : `${v}px`);
    set('--safe-top', sa && csa ? sa.top + csa.top : undefined, 'env(safe-area-inset-top, 0px)');
    set('--safe-bottom', sa?.bottom, 'env(safe-area-inset-bottom, 0px)');
    set('--safe-left', sa ? Math.max(16, sa.left) : undefined, 'max(16px, env(safe-area-inset-left, 0px))');
    set('--safe-right', sa ? Math.max(16, sa.right) : undefined, 'max(16px, env(safe-area-inset-right, 0px))');
    const w = innerWidth;
    const h = wa?.viewportStableHeight || innerHeight;
    const next = { w, h, width: widthClass(w), height: heightClass(h), rotate: needsRotate({ w: innerWidth, h: innerHeight, phone }), fullscreen: !!wa?.isFullscreen };
    root.dataset.w = next.width;
    root.dataset.h = next.height;
    viewport.value = next;
  };
  const schedule = () => { if (!raf) raf = requestAnimationFrame(apply); }; // не чаще раза в кадр (ux/12 §3)
  apply();
  addEventListener('resize', schedule);
  const evs = ['safeAreaChanged', 'contentSafeAreaChanged', 'viewportChanged', 'fullscreenChanged'];
  for (const e of evs) wa?.onEvent?.(e, schedule);
  return () => {
    removeEventListener('resize', schedule);
    for (const e of evs) wa?.offEvent?.(e, schedule);
  };
}

/** Старт полноэкранного режима; неудачный вызов — игра продолжает в обычном режиме без сообщения. */
export function startFullscreen(wa: TgWebApp | null, configDefault: boolean): void {
  if (!wa) return;
  const api8 = versionAtLeast(wa, '8.0');
  if (api8) wa.lockOrientation?.(); // TG-22: запрет поворота; если не сработал — OVL-ROTATE
  if (!shouldRequestFullscreen({ api8, stored: storage.get(FULLSCREEN_KEY), configDefault })) return;
  // Отказ (UNSUPPORTED, ALREADY_FULLSCREEN и т. п.) — тихо остаёмся в expand(), без тоста.
  const failed = (e?: unknown) => {
    wa.offEvent?.('fullscreenFailed', failed);
    const error = (e as { error?: string } | undefined)?.error ?? 'exception';
    track('fullscreen_toggle', { state: 'off', source: 'start_failed', screen: 'SCR-BOOT', viewport_h: Math.round(innerHeight), error });
    try { wa.expand?.(); } catch { /* уже развёрнуто */ }
  };
  wa.onEvent?.('fullscreenFailed', failed);
  try { wa.requestFullscreen?.(); } catch { failed(); }
}

/** Переключатель «Во весь экран» в SCR-SETTINGS (любая платформа с Bot API 8.0, ВР-84). */
export function canToggleFullscreen(wa: TgWebApp | null): boolean {
  return !!wa && versionAtLeast(wa, '8.0');
}

export function setFullscreen(wa: TgWebApp | null, on: boolean, screenId: string): void {
  if (!wa) return;
  track('fullscreen_toggle', { state: on ? 'on' : 'off', source: 'settings', screen: screenId, viewport_h: Math.round(innerHeight) });
  storage.set(FULLSCREEN_KEY, on ? 'on' : 'off');
  if (on) wa.requestFullscreen?.();
  else wa.exitFullscreen?.();
}
